/**
 * CreateEnvironmentModal — Modal de criação de ambiente/agente
 * Extraído do monolito AgentesPage.tsx
 * Mantém 100% da funcionalidade original
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../../api/client';
import { showToast } from '../../components/Toast';
import { Button, Select, Input, Spinner, Badge, SectionDivider, Icon } from '../../components/ui';
import { T } from '../../styles/tokens';
import { formatMemory, formatStorage, formatCpu } from '../../utils/format';
import { PROVIDER_OPTIONS, PROVIDER_MODELS } from '../../constants/providers';
import type { Environment } from './types';

const AVAILABLE_SKILLS = [
  { id: 'weather', label: 'Previsao do tempo' },
  { id: 'trello', label: 'Gerenciar Trello boards' },
  { id: 'clawhub', label: 'Buscar e instalar skills' },
  { id: 'openai-whisper-api', label: 'Transcrever audio' },
  { id: 'openai-image-gen', label: 'Gerar imagens' },
];

interface BrainTemplate {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

function CreateEnvironmentModal({ onClose, onSuccess, onQuotaExceeded, currentEnvCount, maxEnvAllowed, environments: existingEnvs = [] }: { onClose: () => void; onSuccess: () => void; onQuotaExceeded?: () => void; currentEnvCount?: number; maxEnvAllowed?: number; environments?: Environment[] }) {
  const atQuotaLimit = typeof currentEnvCount === 'number' && typeof maxEnvAllowed === 'number' && currentEnvCount >= maxEnvAllowed;
  // Modo do ambiente: novo ou existente
  // Se já tem ambientes: default = 'existing' com o primeiro ambiente criado selecionado
  const oldestEnv = existingEnvs.length > 0
    ? [...existingEnvs].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())[0]
    : null;
  const [envMode, setEnvMode] = useState<'new' | 'existing'>(existingEnvs.length === 0 ? 'new' : 'existing');
  const [selectedEnvId, setSelectedEnvId] = useState(oldestEnv ? (oldestEnv.id || '').replace(/^ws-/, '') : '');
  useEffect(() => { const prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; const mc = document.querySelector('.main-content') as HTMLElement|null; const prevMc = mc?.style.overflow||''; if(mc) mc.style.overflow='hidden'; return () => { document.body.style.overflow = prev; if(mc) mc.style.overflow=prevMc; }; }, []);
  // Basic fields — nome da maquina gerado automaticamente (usuario pode editar)
  const nextNum = existingEnvs.length + 1;
  const [name, setName] = useState(`Maquina ${nextNum}`);
  const [agentName, setAgentName] = useState('');
  const [plan, setPlan] = useState('free');
  const [image, setImage] = useState('');
  const [images, setImages] = useState<Array<{ id: string; name: string; full: string }>>([]);
  
  // Plan configs from DB
  const [planConfigs, setPlanConfigs] = useState<any[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);

  // Brain
  const [brainMode, setBrainMode] = useState<'clean' | 'custom'>('clean');
  const [selectedBrain, setSelectedBrain] = useState('');
  const [brainSearch, setBrainSearch] = useState('');
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [brains, setBrains] = useState<BrainTemplate[]>([]);
  const [brainsLoading, setBrainsLoading] = useState(true);

  // LLM
  const [showLLM, setShowLLM] = useState(false);
  // Accordion: fechar todas as secoes exceto a que esta sendo aberta
  const closeAllSections = (except?: string) => {
    if (except !== 'funcao') setShowFuncao(false);
    if (except !== 'channels') setShowChannels(false);
    if (except !== 'env') setShowEnvSection(false);
    if (except !== 'llm') setShowLLM(false);
    if (except !== 'skills') setShowSkills(false);
    if (except !== 'config') setShowInstallConfig(false);
  };
  const [provider, setProvider] = useState('elon-router');
  const [model, setModel] = useState('auto');

  // Custom LLM providers from DB
  const [customProviderOptions, setCustomProviderOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [customProviderModels, setCustomProviderModels] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  const [apiKey, setApiKey] = useState('');
  const [customBaseUrl, setCustomBaseUrl] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [routerToken, setRouterToken] = useState('');
  const [routerTokenLoading, setRouterTokenLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Resources
  const [showResources, setShowResources] = useState(false);

  // Docker
  const [showDocker, setShowDocker] = useState(false);

  // Backup
  const [showBackup, setShowBackup] = useState(false);
  const [backups, setBackups] = useState<any[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(true);
  const [selectedBackup, setSelectedBackup] = useState<string>('');

  // Skills
  const [showSkills, setShowSkills] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  // Channels
  const [showFuncao, setShowFuncao] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const [showInstallConfig, setShowInstallConfig] = useState(false);
  const [showEnvSection, setShowEnvSection] = useState(false);
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [telegramUserId, setTelegramUserId] = useState<number | null>(null);
  const [telegramVerifying, setTelegramVerifying] = useState(false);
  const [telegramVerified, setTelegramVerified] = useState(false);
  const [telegramError, setTelegramError] = useState('');
  const [telegramBotName, setTelegramBotName] = useState('');

  // User plan (for resource gating)
  const [userPlan, setUserPlan] = useState('free');
  const [customInstancesEnabled, setCustomInstancesEnabled] = useState(false);

  // UI
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [showInfo, setShowInfo] = useState(false);

  // Load user plan
  useEffect(() => {
    api.get<any>('/subscription/me').then(res => {
      const sub = res?.subscription;
      if (!sub || !sub.id) {
        // Sem subscription — marcar como 'none' pra bloquear criação
        setUserPlan('none');
        setCustomInstancesEnabled(false);
        return;
      }
      const slug = sub.plan_id || sub.plan || res?.plan?.slug || 'free';
      setUserPlan(slug.toLowerCase());
      // Extract custom_instances_enabled from plan data
      const planData = res?.plan || sub.plan_data || {};
      setCustomInstancesEnabled(planData.custom_instances_enabled === 1 || planData.custom_instances_enabled === true);
      const wLimit = planData.workspaces_limit ?? sub.workspaces_limit ?? 1;
      // workspacesLimit não é usado dentro do modal — apenas no componente pai
      // Auto-select max allowed plan
      const order = ['starter', 'pro', 'business', 'enterprise'];
      const maxIdx = order.indexOf(slug.toLowerCase());
      if (maxIdx >= 0) setPlan(order[maxIdx]); // Auto-select o melhor servidor que o plano suporta
    }).catch(() => { setUserPlan('none'); setCustomInstancesEnabled(false); });
  }, []);

  // Load backups do cliente
  useEffect(() => {
    api.get<any>('/cloud/workspaces/backups/all').then(res => {
      setBackups(res.backups || []);
    }).catch(() => {}).finally(() => setBackupsLoading(false));
  }, []);

  // Load docker images + select default
  useEffect(() => {
    api.get<any>('/cloud/workspaces/docker-images').then(res => {
      const imgs = res.images || [];
      setImages(imgs);
      // Select the is_default image first
      const defaultImg = imgs.find((i: any) => i.isDefault);
      if (defaultImg) { setImage(defaultImg.full); return; }
      // Fallback: first image with "openclaw" tag
      const ocImg = imgs.find((i: any) => (Array.isArray(i.tags) ? i.tags : []).includes('openclaw'));
      if (ocImg) setImage(ocImg.full);
      else if (imgs.length > 0) setImage(imgs[0].full);
    }).catch(() => {});
  }, []);

  // Load plan configs from DB
  useEffect(() => {
    api.get<any>('/cloud/workspaces/plan-configs/all').then(res => {
      if (res?.configs) {
        setPlanConfigs(res.configs);
        // Set initial selectedConfig based on default plan
        const defaultConfig = res.configs.find((c: any) => (c.plan_slug) === 'free');
        if (defaultConfig) setSelectedConfig(defaultConfig);
      }
    }).catch(() => {});
  }, []);

  // Update selectedConfig when plan changes
  useEffect(() => {
    if (planConfigs.length > 0) {
      const config = planConfigs.find(c => (c.plan_slug) === plan);
      setSelectedConfig(config || null);
    }
  }, [plan, planConfigs]);

  // Load brains
  useEffect(() => {
    api.get<{ cerebros: BrainTemplate[] }>('/cloud/cerebros')
      .then(res => setBrains(res?.cerebros || (Array.isArray(res) ? res : [])))
      .catch(() => setBrains([]))
      .finally(() => setBrainsLoading(false));
  }, []);

  // Auto-select first model when provider changes
  // Load custom LLM providers from DB (replaces hardcoded list when available)
  useEffect(() => {
    api.get<any>('/cloud/workspaces/llm-providers')
      .then(res => {
        const providers = res?.providers || res?.data || [];
        if (Array.isArray(providers) && providers.length > 0) {
          const opts = providers.map((p: any) => ({
            value: p.id,
            label: p.name + (p.is_default ? ' (Recomendado)' : ''),
          }));
          setCustomProviderOptions(opts);
          const modelsMap: Record<string, Array<{ value: string; label: string }>> = {};
          providers.forEach((p: any) => {
            const models = Array.isArray(p.models) ? p.models : [];
            const parsed = models.map((m: any) => typeof m === 'string' ? { value: m, label: m } : { value: m.value || m.id || m, label: m.label || m.name || m.value || m });
            // Sempre incluir "Automático" como primeira opção nos providers do banco
            const hasAuto = parsed.some((m: any) => m.value === 'auto');
            modelsMap[p.id] = hasAuto ? parsed : [{ value: 'auto', label: 'Automático (melhor modelo disponível)' }, ...parsed];
          });
          setCustomProviderModels(modelsMap);
          // Auto-select default provider + default model
          const defaultProv = providers.find((p: any) => p.is_default);
          if (defaultProv) {
            setProvider(defaultProv.id);
            if (defaultProv.default_model) {
              setModel(defaultProv.default_model);
              initialDefaultApplied.current = true;
            }
          } else {
            setProvider(providers[0].id);
          }
        }
      })
      .catch(() => {});
  }, []);

  // Custom DB providers + native providers + custom server option
  const allProviderOptions = [...customProviderOptions, ...PROVIDER_OPTIONS];
  const allProviderModels: Record<string, Array<{ value: string; label: string }>> = { ...PROVIDER_MODELS, ...customProviderModels };

  const initialDefaultApplied = useRef(false);
  useEffect(() => {
    // Não sobrescrever se acabamos de aplicar o default_model do banco
    if (initialDefaultApplied.current) {
      initialDefaultApplied.current = false;
      return;
    }
    const models = allProviderModels[provider] || [];
    if (models.length > 0) setModel(models[0].value);
  }, [provider, customProviderModels]);

  // Fetch Elon Router token
  useEffect(() => {
    api.get<any>('/router/tokens')
      .then(res => {
        const tokens = res?.tokens || res?.data || [];
        const live = Array.isArray(tokens) ? tokens.find((t: any) => t.key?.startsWith('ert_live_') || t.token?.startsWith('ert_live_')) : null;
        if (live) setRouterToken(live.key || live.token || '');
      })
      .catch(() => {})
      .finally(() => setRouterTokenLoading(false));
  }, []);

  const toggleSkill = (id: string) => {
    setSelectedSkills(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  useEffect(() => {
    if (telegramToken.trim().length > 20) {
      fetch(`https://api.telegram.org/bot${telegramToken.trim()}/getMe`)
        .then(r => r.json())
        .then((data: any) => { if (data?.ok) setTelegramBotName(data.result.username); })
        .catch(() => {});
    } else {
      setTelegramBotName('');
      setTelegramVerified(false);
      setTelegramUserId(null);
    }
  }, [telegramToken]);

  const isCustomDbProvider = customProviderOptions.some(o => o.value === provider);
  const isCustomServer = provider === 'custom';
  const needsApiKey = !isCustomDbProvider && !isCustomServer && !routerToken && model !== 'auto'; // native providers need API key (unless Elon Router token exists or model is auto)
  const canSubmit = envMode === 'existing'
    ? (selectedEnvId && agentName.trim())
    : (name.trim() && agentName.trim() && image && (!needsApiKey || apiKey.trim()) && (!isCustomServer || (apiKey.trim() && customBaseUrl.trim())) && !atQuotaLimit);

  const handleVerifyTelegram = async () => {
    setTelegramVerifying(true);
    setTelegramError('');
    setTelegramVerified(false);
    setTelegramUserId(null);
    try {
      const username = telegramUsername.replace(/^@/, '').trim();
      if (!username) { setTelegramError('Informe o @ do usuario'); setTelegramVerifying(false); return; }
      const res = await api.post<any>('/cloud/workspaces/telegram/verify-user', {
        bot_token: telegramToken.trim(),
        username,
      });
      if (res?.user_id) {
        setTelegramUserId(res.user_id);
        setTelegramVerified(true);
      } else {
        setTelegramError(res?.error?.message || 'Usuario nao encontrado. Envie /start para o bot primeiro.');
      }
    } catch (err: any) {
      setTelegramError(err?.message || 'Erro ao verificar. Tente novamente.');
    } finally {
      setTelegramVerifying(false);
    }
  };

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit) return;
    setCreating(true);
    setError('');
    setProgress(25);

    try {
      const iv = setInterval(() => setProgress(p => Math.min(p + 10, 90)), 2000);

      if (envMode === 'existing' && selectedEnvId) {
        // Criar agente em ambiente existente
        await api.post(`/cloud/workspaces/${selectedEnvId}/agents`, {
          name: agentName.trim(),
          brain: brainMode === 'clean' ? 'clean' : selectedBrain,
          provider,
          model,
          api_key: needsApiKey && apiKey.trim() ? apiKey.trim() : undefined,
          skills: selectedSkills.length > 0 ? selectedSkills : undefined,
          telegram_token: telegramToken.trim() || undefined,
          telegram_user_id: telegramUserId || undefined,
          telegram_username: telegramUsername.replace(/^@/, '').trim() || undefined,
        });
        clearInterval(iv);
        setProgress(100);
        showToast('Agente criado! Reiniciando ambiente...', 'success');
      } else {
        // Criar novo ambiente + agente
        await api.post('/cloud/workspaces', {
          name: name.trim(),
          image,
          plan,
          fromBackupId: selectedBackup || undefined,
          agent: {
            name: agentName.trim(),
            brain: brainMode === 'clean' ? 'clean' : selectedBrain,
            provider,
            model,
            api_key: needsApiKey && apiKey.trim() ? apiKey.trim() : undefined,
            skills: selectedSkills.length > 0 ? selectedSkills : undefined,
            telegram_token: telegramToken.trim() || undefined,
            telegram_user_id: telegramUserId || undefined,
            telegram_username: telegramUsername.replace(/^@/, '').trim() || undefined,
          },
        });
        clearInterval(iv);
        setProgress(100);
        showToast('Ambiente criado com sucesso!', 'success');
      }
      onSuccess();
    } catch (e: any) {
      const msg = e.message || 'Erro ao criar';
      const isNoSub = msg.includes('NO_SUBSCRIPTION') || msg.includes('inscrever em um plano');
      const isQuota = msg.includes('QUOTA_EXCEEDED') || msg.includes('maximo') || msg.includes('máximo') || msg.includes('upgrade');
      if (isNoSub) {
        setError('⚠️ Você precisa se inscrever em um plano para criar um agente. Acesse a página de planos para começar.');
      } else if (isQuota) {
        setError('⚠️ Limite de ambientes atingido. Faça upgrade do seu plano para criar mais.');
      } else {
        setError(msg);
      }
      setCreating(false);
      setProgress(0);
    }
  };

  const PLAN_ORDER = ['free', 'pro', 'business', 'enterprise'];
  const userPlanIdx = Math.max(PLAN_ORDER.indexOf(userPlan), 0);

  const plans = planConfigs.length > 0 ? planConfigs.map(c => {
    const slug = c.plan_slug || 'free';
    return {
      id: slug,
      name: slug === 'free' ? 'Free' : slug === 'pro' ? 'Pro' : slug === 'business' ? 'Business' : 'Enterprise',
      desc: `${formatCpu(c.cpu_limit)}, ${formatMemory(c.memory_limit)}`,
      minPlan: slug,
      config: c,
    };
  }) : [
    // Fallback se API falhar
    { id: 'free', name: 'Free', desc: '0.5 vCPU, 1.5 GB', minPlan: 'free' },
    { id: 'pro', name: 'Pro', desc: '1 vCPU, 2 GB', minPlan: 'pro' },
    { id: 'business', name: 'Business', desc: '2 vCPU, 3 GB', minPlan: 'business' },
    { id: 'enterprise', name: 'Enterprise', desc: '4 vCPU, 4 GB', minPlan: 'enterprise' },
  ];

  const canSelectPlan = (planId: string) => {
    const minIdx = PLAN_ORDER.indexOf(plans.find(p => p.id === planId)?.minPlan || 'free');
    return userPlanIdx >= minIdx;
  };

  const inputStyle: any = {
    width: '100%', padding: '11px 14px', background: T.bgCard,
    border: `1px solid ${T.border}`, borderRadius: 10, color: T.text,
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const selectStyle: any = { ...inputStyle, cursor: 'pointer' };

  const labelStyle: any = {
    display: 'block', fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6,
  };

    return (
    <div
      style={{
        position: 'fixed', inset: 0, background: T.bgOverlay, backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: window.innerWidth <= 640 ? 'stretch' : 'center', justifyContent: 'center', zIndex: 99999999,
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !creating) onClose(); }}
    >
      <style>{`.bottom-nav { display: none !important; }`}</style>
      <div style={{
        background: T.bgModal, borderRadius: window.innerWidth <= 640 ? 0 : 20,
        width: window.innerWidth <= 640 ? '100%' : '90%', maxWidth: window.innerWidth <= 640 ? '100%' : 520,
        border: window.innerWidth <= 640 ? 'none' : `1px solid ${T.border}`, boxShadow: window.innerWidth <= 640 ? 'none' : `0 24px 64px ${T.bgOverlay}`,
        animation: 'slideUp 0.2s ease-out',
        height: window.innerWidth <= 640 ? '100%' : 'auto',
        maxHeight: window.innerWidth <= 640 ? '100%' : '90vh',
        display: 'flex', flexDirection: 'column' as any, overflow: 'hidden',
      }}>
        {/* Header — fixed top */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${T.border}`, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text }}>Criar Agente</h2>
          <Button variant="ghost" onClick={onClose} disabled={creating} style={{
            background: 'none', border: 'none', color: T.textSecondary, cursor: 'pointer', fontSize: 18, padding: 4,
          }} aria-label="Fechar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </Button>
        </div>

        {/* Form wraps scrollable + footer */}
        <form onSubmit={handleCreate} style={{ flex: 1, display: 'flex', flexDirection: 'column' as any, overflow: 'hidden', minHeight: 0 }}>
        {/* Scrollable content — tap outside input dismisses keyboard */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', WebkitOverflowScrolling: 'touch' }}
          onClick={(e: any) => { if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') { (document.activeElement as any)?.blur?.(); } }}>

        {/* Bloqueio: sem plano ativo */}
        {userPlan === 'none' && (
          <div style={{ marginBottom: 20, padding: '16px 20px', borderRadius: 12, background: '#1e1e1e', border: '1px solid #333' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Plano necessário</span>
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>
              Para criar um agente, você precisa de um <span style={{ color: T.primary, fontWeight: 600 }}>plano ativo</span>.
            </div>
            <Button variant="primary" size="md" onClick={() => { window.location.href = '/upgrade'; }} style={{
              width: '100%', borderRadius: 10, fontWeight: 700, fontSize: 14, padding: '12px 20px',
            }}>
              Ver planos disponíveis
            </Button>
          </div>
        )}

        {/* Info expandable (apenas para novo ambiente) */}
        {envMode !== 'existing' && <div style={{ marginBottom: 16 }}>
          <Button variant="ghost" type="button" onClick={() => setShowInfo(!showInfo)} style={{
            display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
            cursor: 'pointer', padding: 0, color: T.textSecondary, fontSize: 12, fontWeight: 600,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            O que e um Ambiente?
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showInfo ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </Button>
          <div style={{ maxHeight: showInfo ? 120 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{
              marginTop: 8, padding: '12px 14px', borderRadius: 10,
              background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
              fontSize: 12, color: T.textSecondary, lineHeight: 1.6,
            }}>
              Um <strong style={{ color: T.text }}>Ambiente</strong> e uma maquina virtual isolada onde seu agente de IA roda 24/7. Cada ambiente tem seu proprio processador, memoria e armazenamento.
            </div>
          </div>
        </div>}

          {/* Quota limit warning (apenas para novo ambiente) */}
          {envMode !== 'existing' && atQuotaLimit && (
            <div style={{
              padding: '20px', borderRadius: 14, marginBottom: 16,
              background: `linear-gradient(135deg, ${T.primaryBg} 0%, ${T.accentBg} 100%)`,
              border: `1px solid ${T.primaryBg}`,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                Voce atingiu o limite do seu plano
              </div>
              <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>
                Seu plano atual permite <strong style={{ color: T.text }}>{maxEnvAllowed} ambiente{maxEnvAllowed !== 1 ? 's' : ''}</strong> e voce ja esta usando <strong style={{ color: T.text }}>{currentEnvCount}/{maxEnvAllowed}</strong>. Faca upgrade para desbloquear mais ambientes, mais recursos e uso ilimitado.
              </div>
              <a
                href="/upgrade"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`, color: T.text, fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', textDecoration: 'none',
                  boxShadow: `0 4px 16px ${T.primaryBg}`,
                  transition: 'all 0.2s',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                Ver planos e fazer upgrade
              </a>
            </div>
          )}

          {/* Nome do Agente — campo principal, sempre visivel */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Nome do Agente *</label>
            <Input type="text" value={agentName} onChange={(v) => setAgentName(v)}
              placeholder="Ex: Elon Parker, Assistente" style={inputStyle} />
          </div>


          {/* ─── Funcao do Agente (colapsavel) ─── */}
          <SectionDivider
              title="Funcao do Agente"
              open={showFuncao}
              onToggle={() => { const next = !showFuncao; setShowFuncao(next); if (next) closeAllSections('funcao'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
              badge={brainMode === 'clean' ? 'Personalizar' : (brains.find(b => b.id === selectedBrain)?.name || 'Selecionar')}
              badgeColor={brainMode === 'clean' ? T.success : (selectedBrain ? T.primary : undefined)}
            />
          <div style={{ maxHeight: showFuncao ? 2000 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{ paddingTop: 8 }}>
            <p style={{ fontSize: 11, color: T.textSecondary, margin: '0 0 8px 0', lineHeight: 1.4 }}>
              Escolha o que seu agente vai fazer. Voce pode alterar depois.
            </p>

            {/* Busca */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <Input type="text" value={brainSearch} onChange={(v) => { setBrainSearch(v.toLowerCase()); if (v && !showAllTemplates) setShowAllTemplates(true); }}
                placeholder="Buscar funcao ou template..." style={{ ...inputStyle, fontSize: 12, paddingLeft: 32 }} />
            </div>

            {/* Grid unico — funcoes fixas + templates da API (expandiveis) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {/* Personalizar = primeiro */}
              {(!brainSearch || 'personalizar'.includes(brainSearch)) && (() => {
                const active = brainMode === 'clean';
                return (
                  <Button variant="ghost" type="button" title="Comece do zero. Configure nome, personalidade, ferramentas e instrucoes do seu jeito."
                    onClick={() => { setBrainMode('clean'); setSelectedBrain(''); }}
                    style={{
                      padding: '10px 6px', borderRadius: 8, cursor: 'pointer',
                      background: active ? T.primaryBg : T.bgSubtle,
                      border: `1.5px solid ${active ? T.primaryBg : T.border}`,
                      color: T.text, textAlign: 'center', transition: 'all 0.12s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? T.primaryBg : T.textSecondary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>Personalizar</span>
                  </Button>
                );
              })()}

              {/* Funcoes fixas */}
              {[
                { id: '_atendimento', icon: 'chat', label: 'Atendimento', desc: 'Responda clientes automaticamente via chat, WhatsApp ou Telegram com respostas inteligentes.' },
                { id: '_vendas', icon: 'cart', label: 'Vendas', desc: 'Qualifique leads, faca follow-up e feche negocios com scripts de vendas otimizados.' },
                { id: '_suporte', icon: 'wrench', label: 'Suporte', desc: 'Resolva problemas tecnicos, responda duvidas e escale tickets automaticamente.' },
                { id: '_assistente', icon: 'user', label: 'Assistente', desc: 'Ajude em tarefas do dia a dia: agenda, lembretes, pesquisas e organizacao.' },
                { id: '_conteudo', icon: 'pen', label: 'Conteudo', desc: 'Crie textos, posts para redes sociais, copys de anuncios e artigos de blog.' },
              ].filter(fn => !brainSearch || fn.label.toLowerCase().includes(brainSearch) || fn.desc.toLowerCase().includes(brainSearch))
              .map(fn => {
                const active = selectedBrain === fn.id;
                return (
                  <Button variant="ghost" key={fn.id} type="button" title={fn.desc}
                    onClick={() => { setBrainMode('custom'); setSelectedBrain(fn.id); }}
                    style={{
                      padding: '10px 6px', borderRadius: 8, cursor: 'pointer',
                      background: active ? T.primaryBg : T.bgSubtle,
                      border: `1.5px solid ${active ? T.primaryBg : T.border}`,
                      color: T.text, textAlign: 'center', transition: 'all 0.12s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke={active ? T.primaryBg : T.textSecondary}
                      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {fn.icon === 'chat' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>}
                      {fn.icon === 'cart' && <><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></>}
                      {fn.icon === 'wrench' && <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>}
                      {fn.icon === 'user' && <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
                      {fn.icon === 'pen' && <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>}
                    </svg>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>{fn.label}</span>
                  </Button>
                );
              })}

              {/* Templates da API — mesmo grid, expandiveis */}
              {(showAllTemplates || brainSearch) && brains.filter(b => !brainSearch || b.name.toLowerCase().includes(brainSearch) || (b.description || '').toLowerCase().includes(brainSearch)).map(b => {
                const active = selectedBrain === b.id;
                const ic = b.icon || 'bot';
                const strokeColor = active ? T.primaryBg : T.textSecondary;
                return (
                  <Button variant="ghost" key={b.id} type="button" title={b.description || b.name}
                    onClick={() => { setBrainMode('custom'); setSelectedBrain(b.id); }}
                    style={{
                      padding: '10px 6px', borderRadius: 8, cursor: 'pointer',
                      background: active ? T.primaryBg : T.bgSubtle,
                      border: `1.5px solid ${active ? T.primaryBg : T.border}`,
                      color: T.text, textAlign: 'center', transition: 'all 0.12s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {ic === 'briefcase' && <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></>}
                      {ic === 'server' && <><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></>}
                      {ic === 'bug' && <><path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3 3 0 0 1 6 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z"/><path d="M12 20v-9M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2M22 13h-4M17.47 9c1.93-.2 3.53-1.9 3.53-4"/><path d="M22 17h-4M6 17H2"/></>}
                      {ic === 'target' && <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>}
                      {ic === 'cpu' && <><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"/></>}
                      {ic === 'handshake' && <><path d="M11 17a4 4 0 0 0 4-4V7"/><path d="M7 7v6a4 4 0 0 0 4 4"/><path d="M20 7h-3a2 2 0 0 0-2 2v0"/><path d="M4 7h3a2 2 0 0 1 2 2v0"/><line x1="1" y1="7" x2="7" y2="7"/><line x1="17" y1="7" x2="23" y2="7"/></>}
                      {ic === 'cloud' && <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>}
                      {ic === 'dollar' && <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>}
                      {ic === 'scale' && <><path d="M16 3l5 5-5 5"/><path d="M21 8H9M8 21l-5-5 5-5"/><path d="M3 16h12"/></>}
                      {ic === 'pen' && <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></>}
                      {ic === 'type' && <><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></>}
                      {ic === 'bitcoin' && <><path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042l-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893l-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042l.348-1.97M7.48 20.364l3.126-17.727"/></>}
                      {ic === 'heart' && <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>}
                      {ic === 'bar-chart' && <><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></>}
                      {ic === 'database' && <><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></>}
                      {ic === 'trending-up' && <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>}
                      {ic === 'git-branch' && <><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></>}
                      {ic === 'shopping-bag' && <><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></>}
                      {ic === 'mail' && <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>}
                      {ic === 'code' && <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>}
                      {ic === 'feather' && <><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></>}
                      {ic === 'zap' && <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>}
                      {ic === 'globe' && <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>}
                      {ic === 'megaphone' && <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>}
                      {ic === 'shield' && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>}
                      {ic === 'lock' && <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>}
                      {ic === 'layout' && <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></>}
                      {ic === 'clipboard' && <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></>}
                      {ic === 'activity' && <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}
                      {ic === 'alert-triangle' && <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>}
                      {ic === 'video' && <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></>}
                      {ic === 'search' && <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>}
                      {ic === 'eye' && <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                      {ic === 'monitor' && <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>}
                      {ic === 'share' && <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></>}
                      {ic === 'terminal' && <><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></>}
                      {ic === 'languages' && <><path d="M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1"/><path d="M22 22l-5-10-5 10M14 18h6"/></>}
                      {ic === 'palette' && <><circle cx="13.5" cy="6.5" r="1.5"/><circle cx="17.5" cy="10.5" r="1.5"/><circle cx="8.5" cy="7.5" r="1.5"/><circle cx="6.5" cy="12.5" r="1.5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></>}
                      {ic === 'pie-chart' && <><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></>}
                      {ic === 'rocket' && <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></>}
                      {/* fallback bot */}
                      {!['briefcase','server','bug','target','cpu','handshake','cloud','dollar','scale','pen','type','bitcoin','heart','bar-chart','database','trending-up','git-branch','shopping-bag','mail','code','feather','zap','globe','megaphone','shield','lock','layout','clipboard','activity','alert-triangle','video','search','eye','monitor','share','terminal','languages','palette','pie-chart','rocket'].includes(ic) && <><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="4"/><line x1="8" y1="16" x2="8" y2="16.01"/><line x1="16" y1="16" x2="16" y2="16.01"/></>}
                    </svg>
                    <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{b.name}</span>
                  </Button>
                );
              })}
            </div>

            {/* Botao expandir/retrair */}
            {brains.length > 0 && (
              <Button variant="ghost" type="button" onClick={() => setShowAllTemplates(!showAllTemplates)}
                style={{
                  background: 'none', border: 'none', color: T.primary, cursor: 'pointer',
                  fontSize: 12, fontWeight: 500, padding: '6px 0 0', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showAllTemplates
                    ? <polyline points="18 15 12 9 6 15"/>
                    : <polyline points="6 9 12 15 18 9"/>}
                </svg>
                {showAllTemplates ? 'Mostrar menos' : `Ver todos os templates (${brains.length})`}
              </Button>
            )}
            </div>
          </div>


          {/* ─── Telegram ─── */}
          <SectionDivider
              title="Conectar Telegram"
              open={showChannels}
              onToggle={() => { const next = !showChannels; setShowChannels(next); if (next) closeAllSections('channels'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
              badge={telegramVerified ? 'Conectado' : (telegramToken.trim() ? 'Token informado' : 'Nao configurado')}
              badgeColor={telegramVerified ? T.success : (telegramToken.trim() ? T.warning : undefined)}
            />
          <div style={{ maxHeight: showChannels ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{ paddingTop: 12 }}>

              {/* Tutorial passo a passo — redesign profissional */}
              <div style={{ marginBottom: 16, padding: '16px', borderRadius: 12, background: T.primaryBg, border: `1px solid rgba(41,182,246,0.12)` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: T.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11.944 0A12 12 0 1 0 24 12.056A12.01 12.01 0 0 0 11.944 0Zm5.654 8.153l-1.755 8.27c-.133.587-.48.73-.974.455l-2.69-1.983-1.297 1.25c-.144.143-.264.264-.541.264l.193-2.736 4.98-4.497c.217-.193-.047-.3-.336-.107l-6.155 3.876-2.652-.827c-.576-.18-.588-.576.12-.853l10.369-3.996c.48-.18.9.107.738.883Z" fill="#29B6F6"/></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Conectar ao Telegram</div>
                </div>

                {/* Passos visuais */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { num: '1', text: <>Abra <a href="https://t.me/BotFather" target="_blank" rel="noopener" style={{ color: T.primary, textDecoration: 'none', fontWeight: 600 }}>@BotFather</a> no Telegram</> },
                    { num: '2', text: <>Envie <code style={{ background: T.bgHover, padding: '2px 8px', borderRadius: 5, fontSize: 11, fontFamily: 'monospace', color: T.primary }}>/newbot</code> e crie seu bot</> },
                    { num: '3', text: <>Copie o <strong style={{ color: T.text }}>token</strong> gerado</> },
                    { num: '4', text: <>Cole no campo abaixo</> },
                  ].map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: T.primaryBg, color: T.primary,
                        fontSize: 11, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>{step.num}</div>
                      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5 }}>{step.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Token input */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Bot Token</label>
                <Input type="text" value={telegramToken} onChange={(v) => setTelegramToken(v)}
                  placeholder="Ex: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz" style={inputStyle} />
              </div>

              {/* Verificacao do usuario */}
              {telegramToken.trim() && (
                <div style={{ marginBottom: 12, padding: '12px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.bgSubtle }}>
                  <label style={{ ...labelStyle, marginBottom: 8 }}>Autorizar seu usuario</label>
                  <p style={{ fontSize: 11, color: T.textSecondary, margin: '0 0 8px 0', lineHeight: 1.4 }}>
                    Informe seu @ do Telegram para que apenas voce possa falar com o agente.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 8 }}>
                    <span style={{ padding: '10px 12px', background: T.border, borderRadius: '10px 0 0 10px', border: `1px solid ${T.border}`, borderRight: 'none', color: T.textSecondary, fontSize: 14, fontWeight: 600 }}>@</span>
                    <Input type="text" value={telegramUsername} onChange={(v) => { setTelegramUsername(v.replace(/^@/, '')); setTelegramVerified(false); setTelegramUserId(null); setTelegramError(''); }}
                      placeholder="seu_usuario" style={{ ...inputStyle, borderRadius: '0', borderLeft: 'none', borderRight: 'none', flex: 1 }} />
                    {telegramVerified ? (
                      <span style={{ padding: '10px 16px', borderRadius: '0 10px 10px 0', background: T.successBg, color: T.success, fontSize: 12, fontWeight: 600, whiteSpace: `nowrap`, border: `1px solid ${T.successBg}`, borderLeft: `none` }}>Verificado</span>
                    ) : (
                      <Button variant="ghost" type="button" onClick={handleVerifyTelegram} disabled={telegramVerifying || !telegramUsername.trim()}
                        style={{ padding: '10px 16px', borderRadius: '0 10px 10px 0', background: telegramVerifying || !telegramUsername.trim() ? T.border : `linear-gradient(135deg, ${T.primary}, ${T.accent})`, color: telegramVerifying || !telegramUsername.trim() ? T.textSecondary : '#fff', fontWeight: 600, fontSize: 12, cursor: telegramVerifying || !telegramUsername.trim() ? 'not-allowed' : 'pointer', border: 'none', whiteSpace: 'nowrap', opacity: telegramVerifying ? 0.6 : 1 }}>
                        {telegramVerifying ? 'Verificando...' : 'Verificar'}
                      </Button>
                    )}
                  </div>
                  {telegramVerified && telegramUserId && (
                    <div style={{ fontSize: 12, color: T.success, marginBottom: 4 }}>Autorizado: {telegramUsername} (ID: {telegramUserId})</div>
                  )}
                  {telegramError && (
                    <div style={{ fontSize: 12, color: T.danger, marginBottom: 4 }}>{telegramError}</div>
                  )}
                  {!telegramVerified && !telegramError && (
                    <>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>
                        Envie <code style={{ background: T.border, padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>/start</code> para {telegramBotName ? <a href={`https://t.me/${telegramBotName}?start=verify`} target="_blank" rel="noopener" style={{ color: T.primary, textDecoration: 'underline' }}>@{telegramBotName}</a> : 'o bot'} antes de verificar
                      </div>
                      <div style={{ fontSize: 11, color: T.warning, marginTop: 4 }}>Sem verificacao, qualquer pessoa podera falar com o agente</div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ─── Onde Instalar (colapsavel) ─── */}
          <SectionDivider
              title="Onde Instalar"
              open={showEnvSection}
              onToggle={() => { const next = !showEnvSection; setShowEnvSection(next); if (next) closeAllSections('env'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}
              badge={envMode === 'existing' && selectedEnvId
                ? (existingEnvs.find(e => (e.id || '').replace(/^ws-/, '') === selectedEnvId)?.name || 'Maquina existente')
                : (envMode === 'new' ? 'Nova maquina' : 'Selecionar')}
              badgeColor={envMode === 'existing' && selectedEnvId ? T.success : (envMode === 'new' ? T.primary : undefined)}
            />
          <div style={{ maxHeight: showEnvSection ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{ paddingTop: 12 }}>
              {/* Resumo do ambiente selecionado */}
              {!showEnvSection && envMode === 'existing' && selectedEnvId && (
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>
                  {existingEnvs.find(e => (e.id || '').replace(/^ws-/, '') === selectedEnvId)?.name || 'Ambiente selecionado'}
                </div>
              )}

              {existingEnvs.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <Button variant="ghost" type="button" onClick={() => { setEnvMode('new'); setSelectedEnvId(''); }}
                      style={{
                        padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                        background: envMode === 'new' ? T.primaryBg : T.bgSubtle,
                        border: `2px solid ${envMode === 'new' ? T.primaryBg : T.border}`,
                        color: T.text, fontSize: 13, fontWeight: 600,
                        textAlign: 'center', transition: 'all 0.15s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      <div>Novo Ambiente</div>
                      <div style={{ fontSize: 10, color: T.textSecondary, fontWeight: 400 }}>Criar do zero</div>
                    </Button>
                    <Button variant="ghost" type="button" onClick={() => { setEnvMode('existing'); setSelectedEnvId(oldestEnv ? (oldestEnv.id || '').replace(/^ws-/, '') : ''); }}
                      style={{
                        padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                        background: envMode === 'existing' ? T.primaryBg : T.bgSubtle,
                        border: `2px solid ${envMode === 'existing' ? T.primaryBg : T.border}`,
                        color: T.text, fontSize: 13, fontWeight: 600,
                        textAlign: 'center', transition: 'all 0.15s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                      <div>Ambiente Existente</div>
                      <div style={{ fontSize: 10, color: T.textSecondary, fontWeight: 400 }}>Adicionar agente</div>
                    </Button>
                  </div>

                  {envMode === 'new' && (
                    <div>
                      <label style={labelStyle}>Nome do Ambiente *</label>
                      <Input type="text" value={name} onChange={(v) => setName(v)}
                        placeholder="Ex: Producao, Vendas, Suporte" style={inputStyle} />
                    </div>
                  )}

                  {envMode === 'existing' && (
                    <div>
                      <label style={labelStyle}>Selecionar Ambiente *</label>
                      <Select value={selectedEnvId} onChange={(v) => setSelectedEnvId(v)} placeholder="-- Escolha um ambiente --" options={existingEnvs.map(env => ({
                        value: (env.id || '').replace(/^ws-/, ''),
                        label: `${env.name} (${env.online ? 'Online' : 'Offline'})`,
                      }))} />
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label style={labelStyle}>Nome do Ambiente *</label>
                  <Input type="text" value={name} onChange={(v) => setName(v)}
                    placeholder="Ex: Producao, Vendas, Suporte" style={inputStyle} />
                </div>
              )}
            </div>
          </div>
          {/* ─── LLM ─── */}
          <SectionDivider
              title="LLM"
              open={showLLM}
              onToggle={() => { const next = !showLLM; setShowLLM(next); if (next) closeAllSections('llm'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 3-2 5-3 6v2H8v-2c-1-1-3-3-3-6a7 7 0 017-7z"/><line x1="9" y1="21" x2="15" y2="21"/></svg>}
              badge={model || provider}
              badgeColor={T.purple}
            />
          <div style={{ maxHeight: showLLM ? 800 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{ paddingTop: 12 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Provedor</label>
                <Select value={provider} onChange={(v) => setProvider(v)} style={selectStyle}
                  options={[
                    ...customProviderOptions.map(p => ({ value: p.value, label: `🏢 ${p.label}` })),
                    ...PROVIDER_OPTIONS.filter(p => p.value !== 'custom').map(p => ({ value: p.value, label: p.label })),
                    { value: 'custom', label: '⚙️ Servidor Customizado' },
                  ]} />
              </div>

              {/* Model select (not for custom server) */}
              {!isCustomServer && (allProviderModels[provider] || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Modelo</label>
                  <Select value={model} onChange={(v) => setModel(v)} style={selectStyle}
                    options={(allProviderModels[provider] || []).map(m => ({ value: m.value, label: m.label }))} />
                </div>
              )}

              {/* Custom server fields */}
              {isCustomServer && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Base URL *</label>
                    <Input type="text" value={customBaseUrl} onChange={(v) => setCustomBaseUrl(v)}
                      placeholder="https://api.exemplo.com/v1" disabled={creating} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Modelo</label>
                    <Input type="text" value={customModel} onChange={(v) => setCustomModel(v)}
                      placeholder="nome-do-modelo" disabled={creating} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Chave de API *</label>
                    <Input type="password" value={apiKey} onChange={(v) => setApiKey(v)}
                      placeholder="sk-..." disabled={creating} style={inputStyle} />
                  </div>
                </>
              )}

              {/* Custom DB provider — key managed by admin */}
              {isCustomDbProvider && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10, background: T.successBg,
                  border: `1px solid ${T.successBg}`, fontSize: 12, color: T.textSecondary,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Chave configurada pelo administrador — nao e necessario informar
                </div>
              )}

              {/* Elon Router provider — chave automática */}
              {isCustomDbProvider && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: `${T.success}12`, border: `1px solid ${T.success}30`, marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: T.success, fontWeight: 600 }}>🔑 Token Elon Router será criado automaticamente</span>
                </div>
              )}

              {/* Native provider — needs API key */}
              {needsApiKey && (
                <div>
                  <label style={labelStyle}>Chave de API *</label>
                  <Input type="password" value={apiKey} onChange={(v) => setApiKey(v)}
                    placeholder="Cole sua API key do provedor" disabled={creating} style={inputStyle} />
                </div>
              )}
            </div>
          </div>

          {/* ─── Skills ─── */}
          <SectionDivider
              title="Skills"
              open={showSkills}
              onToggle={() => { const next = !showSkills; setShowSkills(next); if (next) closeAllSections('skills'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>}
              badge={selectedSkills.length > 0 ? `${selectedSkills.length} selecionada${selectedSkills.length > 1 ? 's' : ''}` : 'Nenhuma'}
              badgeColor={selectedSkills.length > 0 ? T.success : undefined}
            />
          <div style={{ maxHeight: showSkills ? 300 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {AVAILABLE_SKILLS.map(skill => (
                <label key={skill.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  borderRadius: 8, cursor: 'pointer',
                  background: selectedSkills.includes(skill.id) ? T.primaryBg : T.bgSubtle,
                  border: `1px solid ${selectedSkills.includes(skill.id) ? T.primaryBg : T.border}`,
                }}>
                  <input type="checkbox" checked={selectedSkills.includes(skill.id)}
                    onChange={() => toggleSkill(skill.id)} style={{ accentColor: T.primary }} />
                  <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{skill.id}</span>
                  <span style={{ fontSize: 11, color: T.textSecondary }}>— {skill.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Canais removido daqui — movido para depois de Funcao do Agente */}

          {/* ─── Configuracao da Maquina (apenas para novo ambiente) ─── */}
          {envMode !== 'existing' && (<>
          <SectionDivider
              title="Configuracao da Maquina"
              open={showInstallConfig}
              onToggle={() => { const next = !showInstallConfig; setShowInstallConfig(next); if (next) closeAllSections('config'); }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}
            />
          <div style={{ maxHeight: showInstallConfig ? 2000 : 0, overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
            <div style={{ paddingTop: 12 }}>

              {/* Recursos */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Recursos</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {plans.map(p => {
                    const allowed = canSelectPlan(p.id);
                    const selected = plan === p.id;
                    const cfg = p.config;
                    return (
                      <label key={p.id} style={{
                        display: 'flex', flexDirection: 'column', gap: 0, padding: '12px 14px',
                        borderRadius: 10, cursor: allowed ? 'pointer' : 'not-allowed',
                        background: selected ? T.primaryBg : T.bgSubtle,
                        border: `1.5px solid ${selected ? T.primaryBg : T.border}`,
                        opacity: allowed ? 1 : 0.45,
                        transition: 'all 0.15s',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input type="radio" name="plan" value={p.id} checked={selected}
                            disabled={!allowed}
                            onChange={() => allowed && setPlan(p.id)}
                            style={{ accentColor: T.primary, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                              {p.name} <span style={{ fontSize: 11, color: T.textSecondary }}>({p.desc})</span>
                            </div>
                          </div>
                          {allowed ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: T.success }}>
                              {p.id === 'free' ? 'Incluso' : 'Incluso no plano'}
                            </span>
                          ) : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: T.textSecondary }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                              </svg>
                              Plano {p.name}+
                            </span>
                          )}
                        </div>
                        {selected && cfg && (
                          <div style={{ marginTop: 8, marginLeft: 28, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', fontSize: 11, color: T.textSecondary }}>
                            <span>RAM: <b style={{ color: T.text }}>{formatMemory(cfg.memory_request)} → {formatMemory(cfg.memory_limit)}</b></span>
                            <span>CPU: <b style={{ color: T.text }}>{formatCpu(cfg.cpu_request)} → {formatCpu(cfg.cpu_limit)}</b></span>
                            <span>Storage: <b style={{ color: T.text }}>{formatStorage(cfg.storage_size || cfg.storage_limit || '1Gi')}</b></span>
                            <span>Replicas: <b style={{ color: T.text }}>{cfg.max_replicas || 1}</b></span>
                            <span>Auto-Suspend: <b style={{ color: cfg.auto_suspend_minutes > 0 ? T.warning : T.success }}>{cfg.auto_suspend_minutes > 0 ? `${cfg.auto_suspend_minutes} min` : 'Sempre ligada'}</b></span>
                            <span>Limite: <b style={{ color: cfg.daily_hours_limit > 0 ? T.warning : T.success }}>{cfg.daily_hours_limit > 0 ? `${cfg.daily_hours_limit}h/dia` : '∞ Ilimitado'}</b></span>
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Imagem Docker */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Imagem Docker</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(() => {
                    const openclawImages = images.filter((img: any) => {
                      const tags = Array.isArray(img.tags) ? img.tags : [];
                      return tags.includes('openclaw');
                    });
                    const displayImages = openclawImages.length > 0 ? openclawImages : images;
                    return displayImages.map((img: any) => {
                      const selected = image === img.full;
                      const isDefault = img.is_default || img.isDefault;
                      return (
                        <label key={img.full} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                          borderRadius: 10, cursor: 'pointer',
                          background: selected ? T.primaryBg : T.bgSubtle,
                          border: `1.5px solid ${selected ? T.primaryBg : T.border}`,
                          transition: 'all 0.15s',
                        }}>
                          <input type="radio" name="docker-image" value={img.full} checked={selected}
                            onChange={() => setImage(img.full)}
                            style={{ accentColor: T.primary, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{img.name}</span>
                              {isDefault && (
                                <span style={{ padding: '1px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.successBg, color: T.success }}>
                                  Recomendado
                                </span>
                              )}
                            </div>
                            {img.description && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{img.description}</div>}
                            <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 3, fontFamily: 'monospace' }}>{img.full}</div>
                          </div>
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Backup */}
              {backups.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>A partir de Backup</label>
                  <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10, lineHeight: 1.5 }}>
                    Crie o ambiente com a configuracao de um backup existente (cerebro, skills, config).
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      borderRadius: 10, cursor: 'pointer',
                      background: !selectedBackup ? T.primaryBg : T.bgSubtle,
                      border: `1.5px solid ${!selectedBackup ? T.primaryBg : T.border}`,
                      transition: 'all 0.15s',
                    }}>
                      <input type="radio" name="backup" value="" checked={!selectedBackup}
                        onChange={() => setSelectedBackup('')}
                        style={{ accentColor: T.primary, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Sem backup (limpo)</div>
                        <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>Comecar do zero</div>
                      </div>
                    </label>
                    {backups.map((b: any) => {
                      const sel = selectedBackup === b.id;
                      const skillCount = Array.isArray(b.skills) ? b.skills.length : 0;
                      const fileCount = Object.keys(b.brain_files || {}).length;
                      return (
                        <label key={b.id} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                          borderRadius: 10, cursor: 'pointer',
                          background: sel ? T.purpleBg : T.bgSubtle,
                          border: `1.5px solid ${sel ? T.purpleBg : T.border}`,
                          transition: 'all 0.15s',
                        }}>
                          <input type="radio" name="backup" value={b.id} checked={sel}
                            onChange={() => setSelectedBackup(b.id)}
                            style={{ accentColor: T.purple, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{b.name}</div>
                            <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>
                              {fileCount} arquivos · {skillCount} skills · {new Date(b.created_at).toLocaleDateString('pt-BR')}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
          </>)}

          {/* Progress bar */}
          {creating && progress > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`, borderRadius: 2, transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 6, textAlign: 'center' }}>Criando ambiente... {progress}%</div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: T.dangerBg, color: T.danger, fontSize: 13 }}>
              {error}
            </div>
          )}

        </div>{/* end scrollable */}

          {/* Buttons — fixed bottom */}
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, flexShrink: 0, display: 'flex', gap: 10, justifyContent: 'flex-end', background: T.bgModal }}>
            <Button variant="ghost" type="button" onClick={onClose} disabled={creating}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={creating || !canSubmit || userPlan === 'none'} loading={creating}
              title={userPlan === 'none' ? 'Você precisa de um plano ativo' : !canSubmit ? `Faltando: ${[
                !agentName.trim() && 'nome do agente',
                envMode !== 'existing' && !name.trim() && 'nome do ambiente',
                envMode !== 'existing' && !image && 'imagem Docker',
                needsApiKey && !apiKey.trim() && 'API key do provedor',
                atQuotaLimit && 'limite de ambientes atingido',
              ].filter(Boolean).join(', ')}` : ''}
            >
              {creating ? 'Criando...' : 'Criar Agente'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══ CONFIRM DELETE MODAL ═══
// Usa ConfirmDialog de ../components/ui

export { CreateEnvironmentModal };
